/**
 * Anthropic model pricing tables
 */

import type { AnthropicModelPricing } from './types';

export const ANTHROPIC_PRICING_VERSION = '2026-10-12-v11'; // Add Claude Haiku 5.5 standard estimates

// Web search tool: $10 per 1,000 searches (model-independent).
// Used as the fallback when a pricing entry does not define `webSearch`.
export const ANTHROPIC_WEB_SEARCH_COST_PER_REQUEST = 10.00 / 1_000;

// Model pricing in USD (per token)
export const ANTHROPIC_MODEL_PRICING: Record<string, AnthropicModelPricing> = {
  // Claude Haiku 5.5: standard rates for requests with <=100K input tokens.
  // https://platform.claude.com/docs/en/models/haiku-5-5/overview
  // >100K requests cost 5x across token categories, including cached tokens.
  // Usage buckets aggregate requests and cannot identify that threshold; never
  // infer it from bucket totals. Daily totals use Cost Report reconciliation.
  'claude-haiku-5-5': {
    baseInput: 0.10 / 1_000_000,
    cacheRead: 0.01 / 1_000_000,
    cacheWrite5m: 0.125 / 1_000_000,
    cacheWrite1h: 0.20 / 1_000_000,
    output: 0.50 / 1_000_000
  },

  // Claude Opus 5.5 - $4/$20, with cache reads at 5% of input.
  // https://platform.claude.com/docs/en/about-claude/pricing
  // https://claude.com/blog/what-a-task-costs-on-opus-5-5
  'claude-opus-5-5': {
    baseInput: 4.00 / 1_000_000,
    cacheRead: 0.20 / 1_000_000,
    cacheWrite5m: 5.00 / 1_000_000,
    cacheWrite1h: 8.00 / 1_000_000,
    output: 20.00 / 1_000_000
  },

  // Claude Opus 5 - $5/$25
  'claude-opus-5': {
    baseInput: 5.00 / 1_000_000,
    cacheRead: 0.50 / 1_000_000,
    cacheWrite5m: 6.25 / 1_000_000,
    cacheWrite1h: 10.00 / 1_000_000,
    output: 25.00 / 1_000_000
  },

  // Claude Fable 5.1 / Mythos 5.1 - $10/$50 with discounted cache reads
  'claude-fable-5-1': {
    baseInput: 10.00 / 1_000_000,
    cacheRead: 0.25 / 1_000_000,
    cacheWrite5m: 12.50 / 1_000_000,
    cacheWrite1h: 20.00 / 1_000_000,
    output: 50.00 / 1_000_000
  },
  'claude-mythos-5-1': {
    baseInput: 10.00 / 1_000_000,
    cacheRead: 0.25 / 1_000_000,
    cacheWrite5m: 12.50 / 1_000_000,
    cacheWrite1h: 20.00 / 1_000_000,
    output: 50.00 / 1_000_000
  },

  // Claude Fable 5 / Mythos 5 - $10/$50
  'claude-fable-5': {
    baseInput: 10.00 / 1_000_000,
    cacheRead: 1.00 / 1_000_000,
    cacheWrite5m: 12.50 / 1_000_000,
    cacheWrite1h: 20.00 / 1_000_000,
    output: 50.00 / 1_000_000
  },
  'claude-mythos-5': {
    baseInput: 10.00 / 1_000_000,
    cacheRead: 1.00 / 1_000_000,
    cacheWrite5m: 12.50 / 1_000_000,
    cacheWrite1h: 20.00 / 1_000_000,
    output: 50.00 / 1_000_000
  },

  // Claude Sonnet 5 - launch pricing became the permanent standard price on 2026-09-01.
  'claude-sonnet-5': {
    baseInput: 2.00 / 1_000_000,
    cacheRead: 0.20 / 1_000_000,
    cacheWrite5m: 2.50 / 1_000_000,
    cacheWrite1h: 4.00 / 1_000_000,
    output: 10.00 / 1_000_000
  },

  // Claude Opus 4.8/4.7/4.6 Models - Opus $5/$25
  'claude-opus-4-8': {
    baseInput: 5.00 / 1_000_000,
    cacheRead: 0.50 / 1_000_000,
    cacheWrite5m: 6.25 / 1_000_000,
    cacheWrite1h: 10.00 / 1_000_000,
    output: 25.00 / 1_000_000
  },
  'claude-opus-4-7': {
    baseInput: 5.00 / 1_000_000,
    cacheRead: 0.50 / 1_000_000,
    cacheWrite5m: 6.25 / 1_000_000,
    cacheWrite1h: 10.00 / 1_000_000,
    output: 25.00 / 1_000_000
  },
  'claude-opus-4-6': {
    baseInput: 5.00 / 1_000_000,
    cacheRead: 0.50 / 1_000_000,
    cacheWrite5m: 6.25 / 1_000_000,
    cacheWrite1h: 10.00 / 1_000_000,
    output: 25.00 / 1_000_000
  },
  'claude-sonnet-4-6': {
    baseInput: 3.00 / 1_000_000,
    cacheRead: 0.30 / 1_000_000,
    cacheWrite5m: 3.75 / 1_000_000,
    cacheWrite1h: 6.00 / 1_000_000,
    output: 15.00 / 1_000_000
  },

  // Claude 4.5 Models — Opus $5/$25, Sonnet $3/$15, Haiku $1/$5
  'claude-opus-4-5-20251101': {
    baseInput: 5.00 / 1_000_000,
    cacheRead: 0.50 / 1_000_000,
    cacheWrite5m: 6.25 / 1_000_000,
    cacheWrite1h: 10.00 / 1_000_000,
    output: 25.00 / 1_000_000
  },
  'claude-sonnet-4-5-20250929': {
    baseInput: 3.00 / 1_000_000,
    cacheRead: 0.30 / 1_000_000,
    cacheWrite5m: 3.75 / 1_000_000,
    cacheWrite1h: 6.00 / 1_000_000,
    output: 15.00 / 1_000_000
  },
  'claude-haiku-4-5-20251001': {
    baseInput: 1.00 / 1_000_000,
    cacheRead: 0.10 / 1_000_000,
    cacheWrite5m: 1.25 / 1_000_000,
    cacheWrite1h: 2.00 / 1_000_000,
    output: 5.00 / 1_000_000
  },

  // Claude 4.0/4.1 Models — Opus $15/$75, Sonnet $3/$15
  'claude-opus-4-1-20250805': {
    baseInput: 15.00 / 1_000_000,
    cacheRead: 1.50 / 1_000_000,
    cacheWrite5m: 18.75 / 1_000_000,
    cacheWrite1h: 30.00 / 1_000_000,
    output: 75.00 / 1_000_000
  },
  'claude-opus-4-20250514': {
    baseInput: 15.00 / 1_000_000,
    cacheRead: 1.50 / 1_000_000,
    cacheWrite5m: 18.75 / 1_000_000,
    cacheWrite1h: 30.00 / 1_000_000,
    output: 75.00 / 1_000_000
  },
  'claude-sonnet-4-20250514': {
    baseInput: 3.00 / 1_000_000,
    cacheRead: 0.30 / 1_000_000,
    cacheWrite5m: 3.75 / 1_000_000,
    cacheWrite1h: 6.00 / 1_000_000,
    output: 15.00 / 1_000_000
  },
  'claude-3-7-sonnet-20250219': {
    baseInput: 3.00 / 1_000_000,
    cacheRead: 0.30 / 1_000_000,
    cacheWrite5m: 3.75 / 1_000_000,
    cacheWrite1h: 6.00 / 1_000_000,
    output: 15.00 / 1_000_000
  },

  // Claude 3.5 Sonnet
  'claude-3-5-sonnet-20241022': {
    baseInput: 3.00 / 1_000_000,
    cacheRead: 0.30 / 1_000_000,
    cacheWrite5m: 3.75 / 1_000_000,
    cacheWrite1h: 6.00 / 1_000_000,
    output: 15.00 / 1_000_000
  },
  'claude-3-5-sonnet-20240620': {
    baseInput: 3.00 / 1_000_000,
    cacheRead: 0.30 / 1_000_000,
    cacheWrite5m: 3.75 / 1_000_000,
    cacheWrite1h: 6.00 / 1_000_000,
    output: 15.00 / 1_000_000
  },

  // Claude 3.5 Haiku
  'claude-3-5-haiku-20241022': {
    baseInput: 0.80 / 1_000_000,
    cacheRead: 0.08 / 1_000_000,
    cacheWrite5m: 1.00 / 1_000_000,
    cacheWrite1h: 1.60 / 1_000_000,
    output: 4.00 / 1_000_000
  },

  // Claude 3 Opus
  'claude-3-opus-20240229': {
    baseInput: 15.00 / 1_000_000,
    cacheRead: 1.50 / 1_000_000,
    cacheWrite5m: 18.75 / 1_000_000,
    cacheWrite1h: 30.00 / 1_000_000,
    output: 75.00 / 1_000_000
  },

  // Claude 3 Sonnet
  'claude-3-sonnet-20240229': {
    baseInput: 3.00 / 1_000_000,
    cacheRead: 0.30 / 1_000_000,
    cacheWrite5m: 3.75 / 1_000_000,
    cacheWrite1h: 6.00 / 1_000_000,
    output: 15.00 / 1_000_000
  },

  // Claude 3 Haiku
  'claude-3-haiku-20240307': {
    baseInput: 0.25 / 1_000_000,
    cacheRead: 0.03 / 1_000_000,
    cacheWrite5m: 0.30 / 1_000_000,
    cacheWrite1h: 0.50 / 1_000_000,
    output: 1.25 / 1_000_000
  },

  // Legacy models
  'claude-2.1': {
    baseInput: 8.00 / 1_000_000,
    cacheRead: 8.00 / 1_000_000,
    cacheWrite5m: 8.00 / 1_000_000,
    cacheWrite1h: 8.00 / 1_000_000,
    output: 24.00 / 1_000_000
  },
  'claude-2.0': {
    baseInput: 8.00 / 1_000_000,
    cacheRead: 8.00 / 1_000_000,
    cacheWrite5m: 8.00 / 1_000_000,
    cacheWrite1h: 8.00 / 1_000_000,
    output: 24.00 / 1_000_000
  },
  'claude-instant-1.2': {
    baseInput: 0.80 / 1_000_000,
    cacheRead: 0.80 / 1_000_000,
    cacheWrite5m: 0.80 / 1_000_000,
    cacheWrite1h: 0.80 / 1_000_000,
    output: 2.40 / 1_000_000
  }
};

export const ANTHROPIC_FAST_MODE_PRICING: Record<string, AnthropicModelPricing> = {
  // Claude Opus 5.5 Fast mode - $8/$40; prompt-cache pricing multipliers stack.
  // https://platform.claude.com/docs/en/about-claude/pricing
  'claude-opus-5-5': {
    baseInput: 8.00 / 1_000_000,
    cacheRead: 0.40 / 1_000_000,
    cacheWrite5m: 10.00 / 1_000_000,
    cacheWrite1h: 16.00 / 1_000_000,
    output: 40.00 / 1_000_000
  },
  'claude-opus-5': {
    baseInput: 10.00 / 1_000_000,
    cacheRead: 1.00 / 1_000_000,
    cacheWrite5m: 12.50 / 1_000_000,
    cacheWrite1h: 20.00 / 1_000_000,
    output: 50.00 / 1_000_000
  },
  'claude-opus-4-8': {
    baseInput: 10.00 / 1_000_000,
    cacheRead: 1.00 / 1_000_000,
    cacheWrite5m: 12.50 / 1_000_000,
    cacheWrite1h: 20.00 / 1_000_000,
    output: 50.00 / 1_000_000
  },
  'claude-opus-4-7': {
    baseInput: 30.00 / 1_000_000,
    cacheRead: 3.00 / 1_000_000,
    cacheWrite5m: 37.50 / 1_000_000,
    cacheWrite1h: 60.00 / 1_000_000,
    output: 150.00 / 1_000_000
  },
  'claude-opus-4-6': {
    baseInput: 30.00 / 1_000_000,
    cacheRead: 3.00 / 1_000_000,
    cacheWrite5m: 37.50 / 1_000_000,
    cacheWrite1h: 60.00 / 1_000_000,
    output: 150.00 / 1_000_000
  }
};
